// API-Client fuer die Article-Domaene (Session 22).
// Liest/Schreibt gegen /api/articles/* — admin-only auf Server-Seite
// waehrend der Uebergangs-Phase (flip in Session 23).

import api from "../../../lib/api";
import type { SearchInterpretation } from "../../../components/ui/SearchInterpretationHint";
import type {
  Article,
  ArticleStatus,
  ArticleVariant,
  ArticleWithVariants,
  GenerateVariantsResult,
  LightDarkScope,
  LocalizedString,
  LocalizedStringList,
  PrinteraMotifSettings,
  ArticleDesignDetail,
  ArticleVariantContentOverrides,
} from "./types";

export interface VariantUpdateInput {
  price?: number | null;
  stock?: number | null;
  gtin?: string | null;
  // S49 Child-Maske: materialisierte Child-Texte
  title?: string | null;
  bullets?: string[];
  countryOfOriginOverride?: string | null;
  contentOverrides?: ArticleVariantContentOverrides;
  attributes?: Record<string, string>;
  attributeGroups?: Record<string, string[]>;
}

export interface ArticleListQuery {
  nicheId?: string;
  status?: ArticleStatus;
  cursor?: string;
  limit?: number;
}

export interface ArticleListPage {
  items: Article[];
  nextCursor: string | null;
}

/// Filteroptionen der Artikelübersicht und Zahl der Produkt-Listings.
export interface ArticleFacets {
  products: string[];
  niches: string[];
  total: number;
}

/// GTIN-Pool-Status (flacher Topf pro User). total = free + reserved + used.
/// active/pending spiegeln den GS1-Aktivierungs-Fortschritt: active = bei GS1
/// aktiv, pending = lokal vergeben (used), aber noch nicht aktiviert.
export interface GtinPoolStatus {
  free: number;
  reserved: number;
  used: number;
  total: number;
  active: number;
  pending: number;
  /// Childs aus Produkten mit aktivierter Auto-Zuweisung.
  assignmentTotal: number;
  assignmentAssigned: number;
  assignmentMissing: number;
  /// Bewusst manuell gepflegte Childs ohne GTIN.
  manualMissing: number;
  /// Frühester wartender GS1-Selbst-Retry (ISO) — null, wenn keiner wartet.
  /// Sichtbarkeit statt Dunkel-Takt: der Zähler sagt, WANN es weitergeht.
  activationRetryAt?: string | null;
}

export interface ArticleExplorerBaseQuery {
  cursor?: number;
  limit?: number;
  category?: string;
  /// Mehrfach-Werte als Komma-Liste (Server verknüpft sie mit ODER).
  product?: string;
  niche?: string;
  /// Manuelle Warengruppe (Category.id) inkl. Unterkategorien (Subtree).
  categoryId?: string;
  /// Marktplatz-Status: "live" | "notlive" auf `marketplace` (Default amazon).
  pushed?: string;
  marketplace?: string;
  /// Serverseitige Freitext-Suche (Artikelnummer, Titel, Varianten-SKU/GTIN).
  search?: string;
  /// S53: nur Familien mit mindestens einem Child ohne GTIN.
  gtinMissing?: string;
  /// Amazon-Kontoschutz: "blocked" | "notblocked" (aktive Sperrregeln).
  protection?: string;
  /// Personalisierung: "personalized" | "standard" | "mixed".
  personalization?: string;
}

export type ArticleExplorerSort =
  | "updatedAt"
  | "createdAt"
  | "articleNumber"
  | "name"
  | "status"
  | "productKey";

export interface ArticleExplorerQuery extends ArticleExplorerBaseQuery {
  sort?: ArticleExplorerSort;
  dir?: "asc" | "desc";
}

export interface ArticleExplorerPage {
  items: Article[];
  nextCursor: number | null;
  /// Nur Seite 0 zählt (voller groupBy ist teuer); Folgeseiten: null.
  /// Der Client liest total ohnehin aus pages[0] der Infinite-Query.
  total: number | null;
  /// Wie der Server die Suche verstanden hat (Designnummer, Nische + Nummer, Freitext).
  searchInterpretation?: SearchInterpretation | null;
}

export interface ListingWizardActivity {
  id: string;
  status: "working" | "error";
  phase: "creating" | "images" | "error";
  articleCount: number;
  images: {
    enabled: boolean;
    total: number;
    ready: number;
    waiting: number;
    active: number;
    failed: number;
  };
  gtins: {
    required: number;
    assigned: number;
  };
  error: string | null;
  updatedAt: string;
}

/// Manuelle Warengruppe (flach; der Client baut daraus den verschachtelten Baum).
export interface ManualCategory {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  directDesignCount: number;
}
export interface CategoryCreateInput {
  name: string;
  parentId?: string | null;
}
export interface CategoryUpdateInput {
  name?: string;
  parentId?: string | null;
  sortOrder?: number;
}

export interface ArticleCreateInput {
  nicheId: string;
  name: string;
  slug?: string;
  designAssetIds?: string[];
  productSkuIds?: string[];
  lightDarkScope?: LightDarkScope;
  titleSuggestion?: LocalizedString | string;
  descriptionSuggestion?: LocalizedString | string;
  bulletpoints?: LocalizedStringList | string[];
  nicheKeywords?: string[];
  countryOfOriginOverride?: string | null;
  // Artikelstamm v2: materialisierte Listing-Daten
  title?: string;
  description?: string;
  // Kanal-Titel (Session 48): Amazon-Kern ≤75 / Highlight ≤125 / eBay ≤80 / Kaufland ≤100
  titleAmazon?: string;
  titleDifferentiation?: string;
  titleEbay?: string;
  titleKaufland?: string;
  titleEtsy?: string;
  titlePrintera?: LocalizedString;
  channelTexts?: import("shared/listingText").ChannelTexts;
  bullets?: string[];
  personalized?: boolean;
  backPrint?: boolean;
  status?: ArticleStatus;
  nicheTemplateId?: string | null;
}

export type ArticleUpdateInput = Partial<Omit<ArticleCreateInput, "nicheId">> & {
  expectedUpdatedAt?: string;
};

export interface ArticleFromLibraryInput {
  nicheId: string;
  designAssetIds: string[];
  mode?: "per-design" | "pack";
  productSkuIds?: string[];
  lightDarkScope?: LightDarkScope;
  baseName: string;
}

export const ARTICLE_CSV_CATEGORY_LEVEL_LIMIT = 100;
export type ArticleCsvExportStaticField =
  | "sku"
  | "articleName"
  | "gtin"
  | "weightKg"
  | "componentSku";
export type ArticleCsvCategoryField = `categoryLevel:${number}`;
export type ArticleCsvExportField = ArticleCsvExportStaticField | ArticleCsvCategoryField;

function buildListQuery(q: ArticleListQuery): string {
  const params = new URLSearchParams();
  if (q.nicheId) params.set("nicheId", q.nicheId);
  if (q.status) params.set("status", q.status);
  if (q.cursor) params.set("cursor", q.cursor);
  if (q.limit !== undefined) params.set("limit", String(q.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const articlesApi = {
  list: (query: ArticleListQuery = {}): Promise<ArticleListPage> =>
    api.get(`/api/articles${buildListQuery(query)}`).then((r) => r.data),

  /// Produkt-/Nischen-Optionen und Gesamtzahl (serverseitig verdichtet).
  facets: (): Promise<ArticleFacets> => api.get("/api/articles/facets").then((r) => r.data),

  /// Alle Artikel-IDs zum Anzeige-Filter — „Alle auswählen" trifft die
  /// komplette gefilterte Menge, nicht nur die geladenen Seiten.
  listIds: (
    filter?: {
      category?: string;
      product?: string;
      niche?: string;
      categoryId?: string;
      pushed?: string;
      marketplace?: string;
      search?: string;
      gtinMissing?: string;
      protection?: string;
      personalization?: string;
    } | null,
  ): Promise<string[]> => {
    const params = new URLSearchParams();
    if (filter?.category) params.set("category", filter.category);
    if (filter?.product) params.set("product", filter.product);
    if (filter?.niche) params.set("niche", filter.niche);
    if (filter?.categoryId) params.set("categoryId", filter.categoryId);
    if (filter?.pushed) params.set("pushed", filter.pushed);
    if (filter?.marketplace) params.set("marketplace", filter.marketplace);
    if (filter?.search) params.set("search", filter.search);
    if (filter?.gtinMissing) params.set("gtinMissing", filter.gtinMissing);
    if (filter?.protection) params.set("protection", filter.protection);
    if (filter?.personalization) params.set("personalization", filter.personalization);
    const qs = params.toString();
    return api
      .get(`/api/articles/ids${qs ? `?${qs}` : ""}`)
      .then((r) => (r.data.ids ?? []) as string[]);
  },

  /// Product Explorer: flache Vaterartikel, serverseitig sortiert/paginiert.
  explorer: (q: ArticleExplorerQuery = {}, signal?: AbortSignal): Promise<ArticleExplorerPage> => {
    const p = new URLSearchParams();
    if (q.cursor != null) p.set("cursor", String(q.cursor));
    if (q.limit != null) p.set("limit", String(q.limit));
    if (q.category) p.set("category", q.category);
    if (q.product) p.set("product", q.product);
    if (q.niche) p.set("niche", q.niche);
    if (q.categoryId) p.set("categoryId", q.categoryId);
    if (q.pushed) p.set("pushed", q.pushed);
    if (q.marketplace) p.set("marketplace", q.marketplace);
    if (q.search) p.set("search", q.search);
    if (q.gtinMissing) p.set("gtinMissing", q.gtinMissing);
    if (q.protection) p.set("protection", q.protection);
    if (q.personalization) p.set("personalization", q.personalization);
    if (q.sort) p.set("sort", q.sort);
    if (q.dir) p.set("dir", q.dir);
    const qs = p.toString();
    return api.get(`/api/articles/explorer${qs ? `?${qs}` : ""}`, { signal }).then((r) => r.data);
  },

  listingWizardActivity: (): Promise<{ activities: ListingWizardActivity[] }> =>
    api.get("/api/listing-drafts/activity").then((response) => response.data),

  get: (id: string): Promise<ArticleWithVariants> =>
    api.get(`/api/articles/${id}`).then((r) => r.data.article),

  getDesign: (designKey: string): Promise<ArticleDesignDetail> =>
    api.get(`/api/articles/designs/${encodeURIComponent(designKey)}`).then((r) => r.data.design),

  updateDesign: (
    designKey: string,
    data: { name?: string; shopLabels?: Record<string, string> },
  ): Promise<ArticleDesignDetail> =>
    api
      .patch(`/api/articles/designs/${encodeURIComponent(designKey)}`, data)
      .then((r) => r.data.design),

  create: (data: ArticleCreateInput): Promise<Article> =>
    api.post("/api/articles", data).then((r) => r.data.article),

  fromLibrary: (data: ArticleFromLibraryInput): Promise<Article[]> =>
    api.post("/api/articles/from-library", data).then((r) => r.data.articles),

  update: (id: string, data: ArticleUpdateInput): Promise<Article> =>
    api.patch(`/api/articles/${id}`, data).then((r) => r.data.article),

  updatePrinteraMotifSettings: (
    designKey: string,
    data: { shopLabels: Record<string, string> },
  ): Promise<PrinteraMotifSettings> =>
    api
      .patch(`/api/articles/designs/${encodeURIComponent(designKey)}/printera-settings`, data)
      .then((r) => r.data.settings),

  remove: (id: string): Promise<void> => api.delete(`/api/articles/${id}`).then(() => undefined),

  /** Artikelstamm v2: Parent-Texte auf alle Childs anwenden (Farb-Ersetzung). */
  rematerializeChildren: (id: string): Promise<{ ok: boolean }> =>
    api.post(`/api/articles/${id}/rematerialize-children`).then((r) => r.data),

  generateVariants: (id: string, opts: { force?: boolean } = {}): Promise<GenerateVariantsResult> =>
    api.post(`/api/articles/${id}/generate-variants`, opts).then((r) => r.data.result),

  /// Batch-Delete: mehrere Artikel auf einmal löschen (Varianten kaskadieren).
  /// Manueller GTIN-Zug (S51): vergibt aus dem Pool + aktiviert bei GS1.
  assignGtins: (articleIds: string[]): Promise<{ requested: number; assigned: number }> =>
    api.post("/api/articles/assign-gtins", { articleIds }).then((r) => r.data),
  batchDelete: (articleIds: string[]): Promise<{ deleted: number }> =>
    api.post("/api/articles/batch-delete", { articleIds }).then((r) => r.data),

  /** Read-only-Auswahl-Export: eine CSV-Zeile je Artikelvariante. */
  exportCsv: async (articleIds: string[], fields: ArticleCsvExportField[]): Promise<Blob> => {
    try {
      const response = await api.post(
        "/api/articles/export.csv",
        { articleIds, fields },
        { responseType: "blob" },
      );
      return response.data as Blob;
    } catch (error: any) {
      // Axios liefert bei responseType=blob auch JSON-Fehler als Blob. Für
      // den Toast zurück in die normale API-Fehlerform übersetzen.
      const data = error?.response?.data;
      if (data instanceof Blob && data.type.includes("json")) {
        try {
          const parsed = JSON.parse(await data.text()) as { error?: string };
          error.response.data = parsed;
          if (parsed.error) error.message = parsed.error;
        } catch {
          // Kaputter Fehler-Body: der ursprüngliche Axios-Fehler bleibt.
        }
      }
      throw error;
    }
  },

  /// Bulk: Warengruppe zuordnen (categoryId=null entfernt die Zuordnung).
  assignCategory: (articleIds: string[], categoryId: string | null): Promise<{ updated: number }> =>
    api.post("/api/articles/assign-category", { articleIds, categoryId }).then((r) => r.data),

  /// GTIN-Pool-Status (free/reserved/used/total) für den eingeloggten User.
  /// Speist die GTIN-Knappheits-Badges (Sidebar + Dashboard).
  gtinPoolStatus: (): Promise<GtinPoolStatus> =>
    api.get("/api/gtin-pool/status").then((r) => r.data),

  /// Per-Variant-Override editieren (Preis/Bestand/GTIN).
  // Listing-Bilder (Session B): registrieren (Upload-Key oder Design-Asset),
  // Hauptbild/Reihenfolge setzen, löschen.
  addImage: (
    articleId: string,
    input: {
      s3Key?: string;
      designAssetId?: string;
      colorId?: string | null;
      slot?: number;
      kind?: string;
    },
  ) => api.post(`/api/articles/${articleId}/images`, input).then((r) => r.data.image),

  updateImage: (
    articleId: string,
    imageId: string,
    input: {
      isMain?: boolean;
      sortOrder?: number;
      slot?: number;
      colorId?: string | null;
      kind?: string;
    },
  ) => api.patch(`/api/articles/${articleId}/images/${imageId}`, input).then((r) => r.data),

  /** Artikelstamm v2: Bildsätze aus Mockups generieren (Queue-Job). */
  generateImages: (articleId: string): Promise<{ ok: boolean; queued: boolean }> =>
    api.post(`/api/articles/${articleId}/generate-images`).then((r) => r.data),

  /** Reihenfolge einer Bild-Gruppe (Farbe bzw. farbunabhängig) atomar setzen. */
  reorderImages: (articleId: string, input: { colorId: string | null; imageIds: string[] }) =>
    api.put(`/api/articles/${articleId}/images/reorder`, input).then((r) => r.data),

  /** Fortschritt der Bild-Generierung (Hintergrund-Status-Chip). */
  imagesStatus: (): Promise<{
    total: number;
    withImages: number;
    queue: { waiting: number; active: number };
  }> => api.get("/api/articles/images-status").then((r) => r.data),

  /** Bild-Readiness einer Auswahl (Bulk-Push-Popover): wie viele der
   *  gewählten Artikel haben schon einen fertigen Bildsatz? */
  imagesReadiness: (
    articleIds: string[],
  ): Promise<{
    total: number;
    ready: number;
    generating: number;
    notReadyNumbers: string[];
  }> => api.post("/api/articles/images-readiness", { articleIds }).then((r) => r.data),

  deleteImage: (articleId: string, imageId: string) =>
    api.delete(`/api/articles/${articleId}/images/${imageId}`).then((r) => r.data),

  updateVariant: (
    articleId: string,
    variantId: string,
    data: VariantUpdateInput,
  ): Promise<ArticleVariant> =>
    api.patch(`/api/articles/${articleId}/variants/${variantId}`, data).then((r) => r.data.variant),

  /// Metadata-Generation via AI (Session 24). Synchroner Call, liefert
  /// Vorschlag zurueck — nicht auto-gespeichert. Client zeigt Preview,
  /// User approved ⇒ PATCH Article mit den Feldern.
  generateMetadata: (articleId: string, locale: "de" | "en"): Promise<MetadataSuggestion> =>
    api.post(`/api/articles/${articleId}/generate-metadata`, { locale }).then((r) => r.data),
};

/// Warengruppen/Kategorien — manuelle, verschachtelbare Navigation (ergänzt
/// den automatischen Produkt/Nischen-Baum). Flache Liste vom Server, der
/// Client baut daraus den Baum (siehe lib/buildManualCategoryTree).
export const categoriesApi = {
  list: (): Promise<ManualCategory[]> =>
    api.get("/api/categories").then((r) => r.data.categories ?? []),

  create: (input: CategoryCreateInput): Promise<ManualCategory> =>
    api.post("/api/categories", input).then((r) => r.data.category),

  update: (id: string, input: CategoryUpdateInput): Promise<ManualCategory> =>
    api.patch(`/api/categories/${id}`, input).then((r) => r.data.category),

  remove: (id: string): Promise<void> => api.delete(`/api/categories/${id}`).then(() => undefined),
};

export interface MetadataSuggestion {
  suggestion: {
    title: string;
    description: string;
    bulletpoints: string[];
    keywords: string[];
  };
  analysis: {
    motif: string;
    subjectMatter: string;
    styleHints: string[];
    dominantColors: string[];
    suggestedKeywords: string[];
  };
  cost: { visionEur: number; titleEur: number; totalEur: number };
  providers: {
    vision: { id: string; model: string };
    title: { id: string; model: string };
  };
  durations: { visionMs: number; titleMs: number; totalMs: number };
  cacheHit: boolean;
}
